import { BaseComponent } from '../../lib/BaseComponent.js';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
}

/**
 * Toast Notification Component
 * Displays temporary notification messages
 */
export class Toast extends BaseComponent {
  private static instance: Toast | null = null;
  private messages: ToastMessage[] = [];
  private timers: Map<string, number> = new Map();

  constructor() {
    super();
    Toast.instance = this;
  }

  /**
   * Get or create the singleton instance
   */
  public static getInstance(): Toast {
    if (!Toast.instance) {
      const toast = document.createElement('app-toast') as Toast;
      document.body.appendChild(toast);
    }
    return Toast.instance!;
  }

  /**
   * Show a success toast
   */
  public static success(message: string, duration: number = 3000): void {
    Toast.getInstance().show(message, 'success', duration);
  }

  /**
   * Show an error toast
   */
  public static error(message: string, duration: number = 5000): void {
    Toast.getInstance().show(message, 'error', duration);
  }

  /**
   * Show a warning toast
   */
  public static warning(message: string, duration: number = 4000): void {
    Toast.getInstance().show(message, 'warning', duration);
  }

  /**
   * Show an info toast
   */
  public static info(message: string, duration: number = 3000): void {
    Toast.getInstance().show(message, 'info', duration);
  }

  /**
   * Show a toast message
   */
  public show(message: string, type: ToastType = 'info', duration: number = 3000): void {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const toast: ToastMessage = { id, type, message, duration };

    this.messages.push(toast);
    this.render();

    // Auto-dismiss after duration
    if (duration > 0) {
      const timer = window.setTimeout(() => {
        this.dismiss(id);
      }, duration);
      this.timers.set(id, timer);
    }
  }

  /**
   * Dismiss a specific toast
   */
  public dismiss(id: string): void {
    // Clear timer
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }

    // Remove message
    this.messages = this.messages.filter(m => m.id !== id);
    this.render();
  }

  protected async render(): Promise<void> {
    this.addStyles(this.getStyles());

    const container = this.createElement('div', { className: 'toast-container' });

    this.messages.forEach(toast => {
      const toastEl = this.createElement('div', {
        className: `toast toast-${toast.type}`
      });

      // Icon
      const icon = this.createElement('span', { className: 'toast-icon' }, this.getIcon(toast.type));
      toastEl.appendChild(icon);

      // Message
      const messageEl = this.createElement('span', { className: 'toast-message' }, toast.message);
      toastEl.appendChild(messageEl);

      // Close button
      const closeBtn = this.createElement('button', {
        className: 'toast-close',
        type: 'button'
      }, '×');
      closeBtn.addEventListener('click', () => this.dismiss(toast.id));
      toastEl.appendChild(closeBtn);

      container.appendChild(toastEl);
    });

    this.shadow.innerHTML = '';
    this.shadow.appendChild(container);
  }

  private getIcon(type: ToastType): string {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
        return 'ℹ';
      default:
        return 'ℹ';
    }
  }

  private getStyles(): string {
    return `
      .toast-container {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        gap: 12px;
        max-width: 400px;
      }

      .toast {
        background: white;
        border-radius: 8px;
        padding: 16px;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        display: flex;
        align-items: center;
        gap: 12px;
        animation: slideIn 0.3s ease-out;
        border-left: 4px solid;
      }

      @keyframes slideIn {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      .toast-success {
        border-left-color: #10b981;
      }

      .toast-error {
        border-left-color: #ef4444;
      }

      .toast-warning {
        border-left-color: #f59e0b;
      }

      .toast-info {
        border-left-color: #3b82f6;
      }

      .toast-icon {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 16px;
        flex-shrink: 0;
      }

      .toast-success .toast-icon {
        background: #d1fae5;
        color: #059669;
      }

      .toast-error .toast-icon {
        background: #fee2e2;
        color: #dc2626;
      }

      .toast-warning .toast-icon {
        background: #fef3c7;
        color: #d97706;
      }

      .toast-info .toast-icon {
        background: #dbeafe;
        color: #2563eb;
      }

      .toast-message {
        flex: 1;
        font-size: 14px;
        color: #374151;
        line-height: 1.5;
      }

      .toast-close {
        background: none;
        border: none;
        font-size: 24px;
        line-height: 1;
        color: #9ca3af;
        cursor: pointer;
        padding: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: all 0.2s;
        flex-shrink: 0;
      }

      .toast-close:hover {
        background: #f3f4f6;
        color: #374151;
      }

      @media (max-width: 640px) {
        .toast-container {
          top: 10px;
          right: 10px;
          left: 10px;
          max-width: none;
        }
      }
    `;
  }
}

// Register custom element
customElements.define('app-toast', Toast);

// Initialize singleton on load
if (typeof window !== 'undefined') {
  window.addEventListener('DOMContentLoaded', () => {
    Toast.getInstance();
  });
}
